import React from 'react';
import { FiAward, FiBookOpen, FiThumbsUp, FiTrendingUp } from 'react-icons/fi';
import './ExamResultAnalysis.css';

interface ResultQuestion {
  question_id: string;
  question_order: number;
  question_text: string;
  options: Array<{ option_number: number; option_text: string; }>;
  user_answers: number[];
  correct_answers: number[];
  is_correct: boolean;
  answered_at?: string;
  explanation: string;
  reasoning: string;
}

interface ExamResultAnalysisProps {
  questions: ResultQuestion[];
  totalQuestions: number;
  correctAnswers: number;
  score: number;
}

const ExamResultAnalysis: React.FC<ExamResultAnalysisProps> = ({
  questions,
  totalQuestions,
  correctAnswers,
  score
}) => {
  // 분석 데이터 계산
  const incorrectQuestions = questions.filter(q => q.answered_at && !q.is_correct);
  const unansweredQuestions = questions.filter(q => !q.answered_at);
  const answeredQuestions = questions.filter(q => q.answered_at);
  
  // 틀린 문제의 키워드 분석
  const analyzeIncorrectKeywords = () => {
    const keywordCounts = new Map<string, number>();
    
    incorrectQuestions.forEach(q => {
      const text = q.question_text.toLowerCase();
      
      // AWS 서비스 키워드 추출
      const keywords = [
        { keyword: 'EC2', patterns: ['ec2', '인스턴스', 'instance'] },
        { keyword: 'S3', patterns: ['s3', '스토리지', 'storage', '버킷'] },
        { keyword: 'VPC', patterns: ['vpc', '네트워크', 'network', '서브넷'] },
        { keyword: 'RDS', patterns: ['rds', '데이터베이스', 'database', 'mysql', 'postgresql'] },
        { keyword: 'Lambda', patterns: ['lambda', '서버리스', 'serverless'] },
        { keyword: 'CloudFormation', patterns: ['cloudformation', '배포', 'deployment', '템플릿'] },
        { keyword: 'IAM', patterns: ['iam', '보안', 'security', '권한', '역할'] },
        { keyword: 'ELB', patterns: ['elb', 'alb', 'nlb', '로드밸런서', 'load balancer'] },
        { keyword: 'Auto Scaling', patterns: ['auto scaling', '오토스케일링', '확장'] },
        { keyword: 'CloudWatch', patterns: ['cloudwatch', '모니터링', 'monitoring', '로그'] },
        { keyword: 'Route 53', patterns: ['route 53', 'dns', '도메인'] },
        { keyword: 'CloudFront', patterns: ['cloudfront', 'cdn', '콘텐츠 네트워크'] },
        { keyword: 'EBS', patterns: ['ebs', '블록 스토리지', 'volume'] },
        { keyword: 'EFS', patterns: ['efs', '파일 시스템', 'file system'] },
        { keyword: 'SNS', patterns: ['sns', '알림', 'notification'] },
        { keyword: 'SQS', patterns: ['sqs', '큐', 'queue'] },
        { keyword: 'API Gateway', patterns: ['api gateway', 'api 게이트웨이'] },
        { keyword: 'DynamoDB', patterns: ['dynamodb', 'nosql'] },
        { keyword: 'ElastiCache', patterns: ['elasticache', '캐시', 'redis', 'memcached'] },
        { keyword: 'ECS', patterns: ['ecs', '컸테이너', 'container', 'fargate'] },
        { keyword: 'EKS', patterns: ['eks', 'kubernetes', '쿠버네티스'] }
      ];
      
      keywords.forEach(({ keyword, patterns }) => {
        if (patterns.some(pattern => text.includes(pattern))) {
          const count = keywordCounts.get(keyword) || 0;
          keywordCounts.set(keyword, count + 1);
        }
      });
    });
    
    return Array.from(keywordCounts.entries())
      .map(([keyword, count]) => ({ keyword, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };
  
  const incorrectKeywords = analyzeIncorrectKeywords();
  
  // 정답률 분포 계산
  const getScoreCategory = (score: number) => {
    if (score >= 90) return 'excellent';
    if (score >= 80) return 'good';
    if (score >= 70) return 'average';
    return 'poor';
  };

  const scoreCategory = getScoreCategory(score);
  
  // 문제 유형별 분석 (간단한 키워드 기반)
  const analyzeQuestionTypes = () => {
    const typeAnalysis = new Map<string, { total: number; correct: number }>();
    
    questions.forEach(q => {
      if (!q.answered_at) return;
      
      // 간단한 키워드 기반 분류
      const text = q.question_text.toLowerCase();
      let category = 'general';
      
      if (text.includes('ec2') || text.includes('인스턴스')) category = 'EC2';
      else if (text.includes('s3') || text.includes('스토리지')) category = 'Storage';
      else if (text.includes('vpc') || text.includes('네트워크')) category = 'Networking';
      else if (text.includes('rds') || text.includes('데이터베이스')) category = 'Database';
      else if (text.includes('iam') || text.includes('보안')) category = 'Security';
      else if (text.includes('lambda') || text.includes('서버리스')) category = 'Serverless';
      else if (text.includes('cloudformation') || text.includes('배포')) category = 'Deployment';
      
      const current = typeAnalysis.get(category) || { total: 0, correct: 0 };
      typeAnalysis.set(category, {
        total: current.total + 1,
        correct: current.correct + (q.is_correct ? 1 : 0)
      });
    });
    
    return Array.from(typeAnalysis.entries())
      .map(([type, data]) => ({
        type,
        total: data.total,
        correct: data.correct,
        percentage: Math.round((data.correct / data.total) * 100)
      }))
      .sort((a, b) => b.total - a.total);
  };

  const typeAnalysis = analyzeQuestionTypes();

  return (
    <div className="exam-result-analysis">
      <h3>시험 결과 분석</h3>
      
      {/* 전체 성과 요약 */}
      <div className="performance-summary">
        <div className={`performance-card ${scoreCategory}`}>
          <div className="performance-icon">
            {scoreCategory === 'excellent' && <FiAward aria-hidden="true" />}
            {scoreCategory === 'good' && <FiThumbsUp aria-hidden="true" />}
            {scoreCategory === 'average' && <FiBookOpen aria-hidden="true" />}
            {scoreCategory === 'poor' && <FiTrendingUp aria-hidden="true" />}
          </div>
          <div className="performance-text">
            <h4>
              {scoreCategory === 'excellent' && '우수한 성과!'}
              {scoreCategory === 'good' && '좋은 성과!'}
              {scoreCategory === 'average' && '평균적인 성과'}
              {scoreCategory === 'poor' && '추가 학습 필요'}
            </h4>
            <p>
              {scoreCategory === 'excellent' && 'AWS 전문가 수준의 실력을 보여주셨습니다.'}
              {scoreCategory === 'good' && 'AWS 기본기가 탄탄합니다. 조금만 더 노력하면 완벽해집니다.'}
              {scoreCategory === 'average' && '기본적인 이해는 있지만 더 깊이 있는 학습이 필요합니다.'}
              {scoreCategory === 'poor' && '기초부터 차근차근 학습하시기 바랍니다.'}
            </p>
          </div>
        </div>
      </div>

      {/* 상세 통계 */}
      <div className="detailed-stats">
        <div className="stat-grid">
          <div className="analysis-stat-item correct">
            <div className="analysis-stat-number">{correctAnswers}</div>
            <div className="analysis-stat-label">정답</div>
          </div>
          <div className="analysis-stat-item incorrect">
            <div className="analysis-stat-number">{incorrectQuestions.length}</div>
            <div className="analysis-stat-label">오답</div>
          </div>
          <div className="analysis-stat-item unanswered">
            <div className="analysis-stat-number">{unansweredQuestions.length}</div>
            <div className="analysis-stat-label">미응답</div>
          </div>
          <div className="analysis-stat-item answered">
            <div className="analysis-stat-number">{answeredQuestions.length}</div>
            <div className="analysis-stat-label">응답</div>
          </div>
        </div>
      </div>

      {/* 영역별 분석 */}
      {typeAnalysis.length > 0 && (
        <div className="type-analysis">
          <h4>📋 영역별 성과</h4>
          <div className="type-list">
            {typeAnalysis.map(({ type, total, correct, percentage }) => (
              <div key={type} className="type-item">
                <div className="type-header">
                  <span className="type-name">{type}</span>
                  <span className="type-score">{correct}/{total} ({percentage}%)</span>
                </div>
                <div className="type-progress">
                  <div 
                    className="type-progress-bar"
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: percentage >= 80 ? '#28a745' : percentage >= 60 ? '#ffc107' : '#dc3545'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* 틀린 키워드 분석 */}
      {incorrectKeywords.length > 0 && (
        <div className="keyword-analysis">
          <h4>🔍 틀린 문제 키워드 분석</h4>
          <div className="keyword-list">
            {incorrectKeywords.map(({ keyword, count }) => (
              <div key={keyword} className="keyword-item">
                <div className="keyword-header">
                  <span className="keyword-name">{keyword}</span>
                  <span className="keyword-count">{count}개 문제</span>
                </div>
                <div className="keyword-progress">
                  <div 
                    className="keyword-progress-bar"
                    style={{ 
                      width: `${(count / incorrectQuestions.length) * 100}%`,
                      backgroundColor: '#dc3545'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="keyword-summary">
            <p>틀린 문제 중 가장 많이 나온 키워드들입니다. 이 영역들을 중점적으로 학습하세요.</p>
          </div>
        </div>
      )}

      {/* 학습 권장사항 */}
      <div className="study-recommendations">
        <h4>💡 학습 권장사항</h4>
        <div className="recommendations-list">
          {incorrectQuestions.length > 0 && (
            <div className="recommendation-item">
              <span className="recommendation-icon">📖</span>
              <span>틀린 {incorrectQuestions.length}개 문제의 해설을 다시 한 번 꼼꼼히 읽어보세요.</span>
            </div>
          )}
          
          {incorrectKeywords.length > 0 && (
            <div className="recommendation-item">
              <span className="recommendation-icon">🔍</span>
              <span>
                {incorrectKeywords.slice(0, 3).map(k => k.keyword).join(', ')} 
                영역의 추가 학습이 필요합니다.
              </span>
            </div>
          )}
          
          {typeAnalysis.filter(t => t.percentage < 70).length > 0 && (
            <div className="recommendation-item">
              <span className="recommendation-icon">🎯</span>
              <span>
                {typeAnalysis.filter(t => t.percentage < 70).map(t => t.type).join(', ')} 
                영역의 추가 학습이 필요합니다.
              </span>
            </div>
          )}
          
          {unansweredQuestions.length > 0 && (
            <div className="recommendation-item">
              <span className="recommendation-icon">⏰</span>
              <span>시간 관리를 개선하여 모든 문제에 답할 수 있도록 연습하세요.</span>
            </div>
          )}
          
          {score >= 80 && (
            <div className="recommendation-item">
              <span className="recommendation-icon">🚀</span>
              <span>실전 시험에 도전할 준비가 되었습니다!</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamResultAnalysis;